
/**
 * @file TablePaginationView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

/**
 * An interface definition for TablePaginationActionsProps
 */
interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;

    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

/**
 * A function component for creating TablePaginationView
 * @param props TablePaginationActionsProps
 * @returns 
 */
export function TablePaginationView(props: TablePaginationActionsProps) {

    const theme = useTheme();
    
    const { count, page, rowsPerPage, onPageChange } = props;

    /**
     * Handles First Page Button Click
     * @param event MouseEvent
     */
    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    /**
     * Handles Back Button Click
     * @param event MouseEvent
     */
    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    /**
     * Handles Next Button Click 
     * @param event MouseEvent
     */
    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    /**
     * Handles Last Page Button Click
     * @param event MouseEvent
     */
    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page">
                {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page">
                {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page">
                {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page">
                {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}